import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { matchApi } from '../services/api';
import ContentBlocksEditor from '../components/ContentBlocksEditor';
import ContentBlocksSchemaProvider from '../components/ContentBlocksSchemaProvider';
import { Match, AnyContentBlock } from '../models';

const MatchContentEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [match, setMatch] = useState<Match | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contentBlocks, setContentBlocks] = useState<AnyContentBlock[]>([]);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const fetchMatch = async () => {
      try {
        setLoading(true);
        const response = await matchApi.getById(parseInt(id || '0', 10));
        setMatch(response.data);
        setContentBlocks(response.data.content_blocks || []);
      } catch (error) {
        console.error('Error fetching match:', error);
        setNotification({
          open: true,
          message: 'Failed to load match data',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMatch();
    }
  }, [id]);

  const handleContentBlocksChange = (newBlocks: AnyContentBlock[]) => {
    setContentBlocks(newBlocks);
  };

  const handleSave = async () => {
    if (!match) return;
    
    try {
      setSaving(true);
      // Use PATCH to update only the content_blocks field
      await matchApi.patch(match.id, { content_blocks: contentBlocks });
      setNotification({
        open: true,
        message: 'Content blocks saved successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error saving content blocks:', error);
      setNotification({
        open: true,
        message: 'Failed to save content blocks',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!match) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">Match not found</Typography>
        <Button 
          component={Link} 
          to="/matches" 
          variant="contained" 
          sx={{ mt: 2 }}
        >
          Back to Matches
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/matches" underline="hover">
          Matches
        </MuiLink>
        <MuiLink component={Link} to={`/matches/${match.id}`} underline="hover">
          {match.name || `Match #${match.id}`}
        </MuiLink>
        <Typography color="text.primary">Content Editor</Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5">
              {match.name || `Match #${match.id}`}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {match.series?.name || 'No series'} • {new Date(match.start_date).toLocaleDateString()}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : 'Save Content'}
            </Button>
          </Box>
        </Box>

        <ContentBlocksSchemaProvider>
          <ContentBlocksEditor 
            blocks={contentBlocks} 
            onChange={handleContentBlocksChange} 
          />
        </ContentBlocksSchemaProvider>
      </Paper>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MatchContentEditor;
